import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { JOURNEYS, STAGES, ADOPTION_STEPS, SUCCESS_QUESTIONS, PERSONAS } from "@/data/canvas";
import { ArchitectureStages } from "@/components/ArchitectureStages";
import { LayerLegend } from "@/components/LayerTag";
import { IllustrativeBadge } from "@/components/IllustrativeBadge";
import { NodeStatus } from "@/components/NodeStatus";
import { MillGuide } from "@/components/MillGuide";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1762416964549-c69d31e8c7a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxjb21tdW5pdHklMjBodWIlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODMzNDMwMzN8MA&ixlib=rb-4.1.0&q=85&w=1600";

export default function CanvasHome() {
  return (
    <div className="pb-8">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-stone-200">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-mill-bg via-mill-bg/95 to-mill-bg/70" />
          <div className="grain absolute inset-0 opacity-60" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <div className="max-w-3xl animate-fade-up">
            <div className="mb-5 flex flex-wrap items-center gap-3">
              <IllustrativeBadge />
              <NodeStatus />
            </div>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-mill-ink sm:text-5xl lg:text-6xl">
              People, Places & Practice Canvas
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-mill-muted">
              A private, synthetic design instrument for The Mill — to explore where ordinary software does the ordinary work,
              where a web self-service step reduces friction, and where a KATLAS receipt genuinely earns its place.
            </p>
            <div className="mt-7 rounded-2xl border-l-4 border-mill-moss bg-white/70 p-5 backdrop-blur-sm">
              <p className="font-display text-lg font-semibold text-mill-ink">“Use ordinary software for ordinary work.”</p>
              <p className="mt-1 text-sm text-mill-muted">
                Use KATLAS only where custody, authority or a receipt materially improves a real handover, approval,
                acknowledgement or proof of completion.
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#journeys" className="inline-flex items-center gap-2 rounded-md bg-mill-moss px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-mill-moss-dark" data-testid="hero-explore-journeys">
                Explore the three journeys <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#architecture" className="inline-flex items-center gap-2 rounded-md border border-stone-300 bg-white px-5 py-3 text-sm font-semibold text-mill-ink transition-colors hover:bg-mill-alt" data-testid="hero-architecture">
                See the adoption pathway
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {/* Personas */}
        <section className="py-12" data-testid="personas-section">
          <SectionHead eyebrow="1 · Who's involved" title="Pick a role and walk the day in their shoes" />
          <p className="mb-6 max-w-2xl text-sm text-mill-muted">
            Use the switcher (top right) any time. Each role has a defined authority — what they may request, approve,
            acknowledge or receive. These are fictional characters, not real Mill people.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PERSONAS.map((p) => (
              <div key={p.id} className="flex gap-4 rounded-2xl border border-stone-200 bg-white p-5" data-testid={`persona-card-${p.id}`}>
                <img src={p.avatar} alt={p.name} className="h-14 w-14 shrink-0 rounded-full object-cover" />
                <div>
                  <p className="font-display font-semibold text-mill-ink">{p.name}</p>
                  <p className="text-sm font-medium text-mill-moss">{p.role}</p>
                  <p className="mt-1 text-xs leading-snug text-mill-muted">{p.authority}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-5">
            <p className="mb-4 text-sm font-semibold text-mill-ink">Every action across the canvas is labelled by layer:</p>
            <LayerLegend />
          </div>
        </section>

        {/* Journeys */}
        <section id="journeys" className="scroll-mt-20 py-12" data-testid="journeys-section">
          <SectionHead eyebrow="2 · The journeys" title="Enough interaction to feel real — in under ten minutes" />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {JOURNEYS.map((j) => {
              const Icon = Icons[j.icon] || Icons.Circle;
              return (
                <Link
                  key={j.id}
                  to={j.route}
                  data-testid={`journey-card-${j.id}`}
                  className="group flex flex-col rounded-2xl border border-stone-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F0EA] text-mill-moss">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold leading-snug text-mill-ink">{j.title}</h3>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {j.chain.map((c) => (
                      <span key={c} className="rounded-full bg-mill-alt px-2 py-0.5 text-[11px] text-mill-muted">{c}</span>
                    ))}
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-mill-muted">{j.summary}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-mill-moss group-hover:gap-2.5 transition-all">
                    Open journey <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Ask The Mill */}
        <section id="ask" className="scroll-mt-20 py-12" data-testid="ask-section">
          <SectionHead eyebrow="Guided navigation" title="Find the right current information — without the email chain" />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
            <MillGuide compact />
            <div className="rounded-3xl border border-stone-200 bg-mill-alt/50 p-6">
              <p className="font-display text-sm font-semibold text-mill-ink">Public knowledge should be easy to find.</p>
              <p className="mt-1 text-sm text-mill-muted">Private entitlement should be easy to prove. Consequential actions should only be taken by the right authority.</p>
              <Link to="/journey/community-guide" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-mill-moss hover:gap-2.5 transition-all" data-testid="ask-open-full">
                Open the full guide, content & CAROPS mapping <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Architecture */}
        <section id="architecture" className="scroll-mt-20 py-12" data-testid="architecture-section">
          <SectionHead eyebrow="3 · Architecture progression" title="A staged path — not a technology commitment" />
          <ArchitectureStages />
        </section>

        {/* Adoption pathway */}
        <section className="py-12" data-testid="adoption-section">
          <SectionHead eyebrow="4 · Where this could go" title="An adoption pathway, not a pitch" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            {ADOPTION_STEPS.map((s, i) => (
              <div key={s.title} className="relative rounded-2xl border border-stone-200 bg-white p-5" data-testid={`adoption-step-${i}`}>
                <span className="font-mono text-xs text-mill-moss">Step {i + 1}</span>
                <h4 className="mt-2 font-display text-sm font-semibold text-mill-ink">{s.title}</h4>
                <p className="mt-1 text-xs leading-relaxed text-mill-muted">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Success questions */}
        <section className="pb-4" data-testid="success-section">
          <div className="rounded-3xl border border-stone-200 bg-mill-alt/50 p-7 sm:p-10">
            <SectionHead eyebrow="The test" title="This canvas succeeds when a stakeholder can answer:" />
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {SUCCESS_QUESTIONS.map((q, i) => (
                <li key={q} className="flex items-start gap-3 rounded-xl bg-white p-4 text-sm text-mill-ink" data-testid={`success-q-${i}`}>
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-mill-moss font-mono text-xs text-white">{i + 1}</span>
                  {q}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title }) {
  return (
    <div className="mb-6">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-mill-moss">{eyebrow}</p>
      <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-mill-ink sm:text-3xl">{title}</h2>
    </div>
  );
}
